import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "./SideBar";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faUsers,
  faNewspaper,
  faHeart,
  faCamera,
  faCalendarAlt,
  faMapMarkerAlt,
  faCheckCircle,
  faStar,
  faChartLine,
  faEye,
  faRetweet,
  faComment,
  faQuoteRight,
  faBookmark,
  faSpinner,
  faExternalLinkAlt,
  faBolt,
  faMobileAlt,
  faGlobe,
  faLanguage,
  faClock,
  faFire,
  faLightbulb,
  faSync,
  faDatabase,
  faChevronRight,
  faArrowUp,
  faArrowDown,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL_INFLUENCER;

const CLASS_COLORS = {
  Informational: { bg: "#E3F2FD", color: "#1565C0", border: "#90CAF9" },
  Promotional: { bg: "#FFF3E0", color: "#E65100", border: "#FFCC80" },
  "Personal Opinion": { bg: "#F3E5F5", color: "#7B1FA2", border: "#CE93D8" },
  Social: { bg: "#E8F5E9", color: "#2E7D32", border: "#A5D6A7" },
  "Question/Poll": { bg: "#FFF8E1", color: "#F57F17", border: "#FFE082" },
  Political: { bg: "#FCE4EC", color: "#C62828", border: "#EF9A9A" },
  "News/Updates": { bg: "#E0F7FA", color: "#00695C", border: "#80DEEA" },
};

const getClassColor = (c) =>
  CLASS_COLORS[c] || { bg: "#F5F5F5", color: "#616161", border: "#E0E0E0" };

const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
};

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return "0%";
  return (n * 100).toFixed(2) + "%";
};

const fmtDec = (n, d = 1) => {
  if (n == null || isNaN(n)) return "0";
  return n.toFixed(d);
};

const safeDivide = (a, b) => (b === 0 ? 0 : a / b);

const trimText = (t, max = 120) =>
  t && t.length > max ? t.substring(0, max) + "…" : t || "";

const parseTwitterDate = (str) => {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (d) => {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const fmtDateFull = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS_LABELS = Array.from({ length: 24 }, (_, i) => {
  if (i === 0) return "12AM";
  if (i < 12) return i + "AM";
  if (i === 12) return "12PM";
  return i - 12 + "PM";
});

const getRankColor = (s) => {
  if (s >= 80) return "#28A745";
  if (s >= 60) return "#FFC107";
  if (s >= 40) return "#FF8C00";
  return "#DC3545";
};

/* ═══════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                */
/* ═══════════════════════════════════════════════════════════════ */
const InfluencerAnalytics = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const username = searchParams.get("username") || "";

  const [profile, setProfile] = useState(null);
  const [allTweets, setAllTweets] = useState([]);
  const [summary, setSummary] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");

  /* ── Fetch one page ──────────────────────────────────────────── */
  const fetchPage = useCallback(
    async (page) => {
      try {
        const res = await axios.get(
          `${API_URL}/analytics/${encodeURIComponent(username)}?page=${page}`
        );
        if (res.data?.status === "success") {
          if (!profile && res.data.profile) setProfile(res.data.profile);
          if (res.data.summary && !summary) setSummary(res.data.summary);

          const newTweets = res.data.tweets || [];

          // Merge using Map — keeps all unique tweets, updates duplicates
          setAllTweets((prev) => {
            const tweetMap = new Map();
            prev.forEach((t) => tweetMap.set(t.tweet_id, t));
            newTweets.forEach((t) => tweetMap.set(t.tweet_id, t));
            return Array.from(tweetMap.values());
          });

          setHasNextPage(res.data.has_next_page || false);
          setCurrentPage(page);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Fetch page error:", err);
        return false;
      }
    },
    [username, profile, summary]
  );

  /* ── Initial load ────────────────────────────────────────────── */
  useEffect(() => {
    if (!username) {
      setError("No username provided.");
      setInitialLoading(false);
      return;
    }
    (async () => {
      setInitialLoading(true);
      setError("");
      const ok = await fetchPage(1);
      if (!ok) setError("Failed to fetch analytics data.");
      setInitialLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  /* ── Load next 20 tweets ─────────────────────────────────────── */
  const loadMore = async () => {
    if (loading || !hasNextPage) return;
    setLoading(true);
    await fetchPage(currentPage + 1);
    setLoading(false);
  };

  /* ═══════════════════════════════════════════════════════════════ */
  /*  COMPUTED ANALYTICS                                           */
  /* ═══════════════════════════════════════════════════════════════ */
  const analytics = useMemo(() => {
    if (!profile || allTweets.length === 0) return null;

    const tweets = allTweets;
    const n = tweets.length;
    const followers = profile.metrics?.followers || 0;

    const createdAt = new Date(profile.created_at);
    const accountAgeDays = Math.floor(
      (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
    );
    const accountAgeYears = (accountAgeDays / 365.25).toFixed(1);
    const ffRatio = safeDivide(followers, profile.metrics?.following || 1);

    let totalViews = 0, totalLikes = 0, totalReplies = 0;
    let totalRetweets = 0, totalQuotes = 0, totalBookmarks = 0;

    const enriched = tweets.map((t) => {
      const m = t.metrics || {};
      const views = m.view_count || 0;
      const likes = m.like_count || 0;
      const replies = m.reply_count || 0;
      const retweets = m.retweet_count || 0;
      const quotes = m.quote_count || 0;
      const bookmarks = m.bookmark_count || 0;

      totalViews += views;
      totalLikes += likes;
      totalReplies += replies;
      totalRetweets += retweets;
      totalQuotes += quotes;
      totalBookmarks += bookmarks;

      const engagement = likes + replies + retweets + quotes + bookmarks;
      const engagementRate = safeDivide(engagement, views);
      const viralityScore = 3 * retweets + 2 * replies + 1 * likes + 2 * quotes;
      const reachRatio = safeDivide(views, followers);
      const bookmarkRate = safeDivide(bookmarks, views);
      const bookmarkLikeRatio = safeDivide(bookmarks, likes);

      const dt = parseTwitterDate(t.tweet_created_at);

      return {
        ...t,
        _views: views, _likes: likes, _replies: replies,
        _retweets: retweets, _quotes: quotes, _bookmarks: bookmarks,
        _engagement: engagement, _engagementRate: engagementRate,
        _viralityScore: viralityScore, _reachRatio: reachRatio,
        _bookmarkRate: bookmarkRate, _bookmarkLikeRatio: bookmarkLikeRatio,
        _date: dt,
        _hour: dt ? dt.getUTCHours() : null,
        _day: dt ? dt.getUTCDay() : null,
        _dateKey: dt ? dt.toISOString().split("T")[0] : null,
      };
    });

    const avgViews = safeDivide(totalViews, n);
    const avgLikes = safeDivide(totalLikes, n);
    const avgReplies = safeDivide(totalReplies, n);
    const avgRetweets = safeDivide(totalRetweets, n);
    const avgQuotes = safeDivide(totalQuotes, n);
    const avgBookmarks = safeDivide(totalBookmarks, n);

    const avgEngagementRate = safeDivide(enriched.reduce((s, t) => s + t._engagementRate, 0), n);
    const avgBookmarkRate = safeDivide(enriched.reduce((s, t) => s + t._bookmarkRate, 0), n);
    const avgReachRatio = safeDivide(enriched.reduce((s, t) => s + t._reachRatio, 0), n);

    const topByViews = [...enriched].sort((a, b) => b._views - a._views).slice(0, 2);
    const topByER = [...enriched].sort((a, b) => b._engagementRate - a._engagementRate).slice(0, 2);
    const topByBookmarks = [...enriched].sort((a, b) => b._bookmarks - a._bookmarks).slice(0, 2);
    const topByVirality = [...enriched].sort((a, b) => b._viralityScore - a._viralityScore).slice(0, 3);

    const classMap = {};
    enriched.forEach((t) => {
      const c = t.classification || "Other";
      if (!classMap[c]) classMap[c] = { count: 0, views: 0, likes: 0, replies: 0, retweets: 0, bookmarks: 0, erSum: 0 };
      classMap[c].count++;
      classMap[c].views += t._views;
      classMap[c].likes += t._likes;
      classMap[c].replies += t._replies;
      classMap[c].retweets += t._retweets;
      classMap[c].bookmarks += t._bookmarks;
      classMap[c].erSum += t._engagementRate;
    });

    const classData = Object.entries(classMap)
      .map(([name, d]) => ({
        name, count: d.count, pct: safeDivide(d.count, n),
        avgViews: safeDivide(d.views, d.count),
        avgLikes: safeDivide(d.likes, d.count),
        avgER: safeDivide(d.erSum, d.count),
      }))
      .sort((a, b) => b.count - a.count);

    const hourMap = Array(24).fill(null).map(() => ({ count: 0, erSum: 0 }));
    const dayMap = Array(7).fill(null).map(() => ({ count: 0, erSum: 0, viewSum: 0 }));

    enriched.forEach((t) => {
      if (t._hour != null) { hourMap[t._hour].count++; hourMap[t._hour].erSum += t._engagementRate; }
      if (t._day != null) { dayMap[t._day].count++; dayMap[t._day].erSum += t._engagementRate; dayMap[t._day].viewSum += t._views; }
    });

    const maxHourCount = Math.max(...hourMap.map((h) => h.count), 1);
    const bestHourIdx = hourMap.reduce((best, h, i) =>
      safeDivide(h.erSum, h.count) > safeDivide(hourMap[best].erSum, hourMap[best].count) ? i : best, 0);
    const mostActiveDayIdx = dayMap.reduce((best, d, i) => d.count > dayMap[best].count ? i : best, 0);
    const peakReachDayIdx = dayMap.reduce((best, d, i) =>
      safeDivide(d.viewSum, d.count) > safeDivide(dayMap[best].viewSum, dayMap[best].count) ? i : best, 0);

    const sourceMap = {};
    enriched.forEach((t) => {
      const s = t.source || "Unknown";
      if (!sourceMap[s]) sourceMap[s] = { count: 0, erSum: 0 };
      sourceMap[s].count++; sourceMap[s].erSum += t._engagementRate;
    });
    const sourceData = Object.entries(sourceMap)
      .map(([name, d]) => ({ name, count: d.count, pct: safeDivide(d.count, n), avgER: safeDivide(d.erSum, d.count) }))
      .sort((a, b) => b.count - a.count);

    const langMap = {};
    enriched.forEach((t) => {
      const l = t.lang || "und";
      if (!langMap[l]) langMap[l] = { count: 0, erSum: 0 };
      langMap[l].count++; langMap[l].erSum += t._engagementRate;
    });
    const langData = Object.entries(langMap)
      .map(([code, d]) => ({ code, count: d.count, pct: safeDivide(d.count, n), avgER: safeDivide(d.erSum, d.count) }))
      .sort((a, b) => b.count - a.count);

    const originals = enriched.filter((t) => !t.is_reply);
    const repliesList = enriched.filter((t) => t.is_reply);
    const avgEROriginals = originals.length ? safeDivide(originals.reduce((s, t) => s + t._engagementRate, 0), originals.length) : 0;
    const avgERReplies = repliesList.length ? safeDivide(repliesList.reduce((s, t) => s + t._engagementRate, 0), repliesList.length) : 0;

    const timelineMap = {};
    enriched.forEach((t) => {
      if (!t._dateKey) return;
      if (!timelineMap[t._dateKey]) timelineMap[t._dateKey] = { date: t._dateKey, tweets: 0, views: 0, engagement: 0, erSum: 0 };
      timelineMap[t._dateKey].tweets++;
      timelineMap[t._dateKey].views += t._views;
      timelineMap[t._dateKey].engagement += t._engagement;
      timelineMap[t._dateKey].erSum += t._engagementRate;
    });
    const timeline = Object.values(timelineMap).sort((a, b) => a.date.localeCompare(b.date));
    const maxTimelineViews = Math.max(...timeline.map((d) => d.views), 1);

    return {
      n, accountAgeDays, accountAgeYears, ffRatio,
      totalViews, totalLikes, totalReplies, totalRetweets, totalQuotes, totalBookmarks,
      avgViews, avgLikes, avgReplies, avgRetweets, avgQuotes, avgBookmarks,
      avgEngagementRate, avgBookmarkRate, avgReachRatio,
      topByViews, topByER, topByBookmarks, topByVirality,
      classData, hourMap, maxHourCount, dayMap,
      bestHourIdx, mostActiveDayIdx, peakReachDayIdx,
      sourceData, langData,
      originals: originals.length, replies: repliesList.length,
      avgEROriginals, avgERReplies,
      timeline, maxTimelineViews, enriched,
    };
  }, [profile, allTweets]);

  /* ═══════════════════════════════════════════════════════════════ */
  /*  SUB-COMPONENTS                                               */
  /* ═══════════════════════════════════════════════════════════════ */
  const StatCard = ({ icon, label, value, sub, iconColor }) => (
    <div className="card" style={{ padding: "18px 16px", margin: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${iconColor || "var(--primary-color)"}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FontAwesomeIcon icon={icon} style={{ color: iconColor || "var(--primary-color)", fontSize: "0.9rem" }} />
        </div>
      </div>
      <div style={{ fontSize: "0.78rem", color: "var(--text-gray)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--text-dark)", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: "0.72rem", color: "var(--text-gray)", marginTop: 4 }}>{sub}</div>}
    </div>
  );

  const HighlightCard = ({ label, value, sub, bg }) => (
    <div style={{ backgroundColor: bg || "var(--primary-color)", color: "#fff", padding: "24px 20px", borderRadius: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: "0.85rem", fontWeight: 600, opacity: 0.85 }}>{label}</div>
        <div style={{ fontSize: "2.2rem", fontWeight: 700, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: "0.75rem", marginTop: 6, opacity: 0.7 }}>{sub}</div>}
      </div>
      <div style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FontAwesomeIcon icon={faChartLine} style={{ fontSize: "1.4rem" }} />
      </div>
    </div>
  );

  const MiniTable = ({ title, icon, items, valueKey, valueFormatter }) => (
    <div className="card" style={{ padding: 0, overflow: "hidden", margin: 0 }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-color)", backgroundColor: "var(--background-light)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>{title}</span>
        <FontAwesomeIcon icon={icon} style={{ color: "var(--text-gray)", fontSize: "0.85rem" }} />
      </div>
      {items.map((t, i) => (
        <div key={i} style={{ padding: "14px 16px", borderBottom: i < items.length - 1 ? "1px solid var(--border-color)" : "none" }}>
          <p style={{ fontSize: "0.82rem", color: "var(--text-light)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", margin: 0 }}>
            {trimText(t.text)}
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>{fmtDate(t._date)}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--primary-color)" }}>
                {valueFormatter ? valueFormatter(t[valueKey]) : fmt(t[valueKey])}
              </span>
              {t.url && (
                <a href={t.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary-color)", fontSize: "0.72rem" }}>
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  /* ═══════════════════════════════════════════════════════════════ */
  /*  RENDER                                                       */
  /* ═══════════════════════════════════════════════════════════════ */
  if (initialLoading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-container">
          <div style={{ textAlign: "center", padding: 60 }}>
            <div className="spinner" />
            <p style={{ color: "var(--text-gray)", marginTop: 12 }}>Loading analytics for @{username}...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !profile) {
    return (
      <>
        <SideBar />
        <div className="dashboard-container">
          <div className="alert alert-error">
            {error || "No data available."}
            <button className="btn btn-primary btn-sm" onClick={() => navigate("/influencer-leaderboard")} style={{ marginLeft: 16 }}>
              <FontAwesomeIcon icon={faTrophy} style={{ marginRight: 6 }} />Back to Leaderboard
            </button>
          </div>
        </div>
      </>
    );
  }

  const a = analytics;

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        {/* ═══ HEADER ══════════════════════════════════════════════ */}
        <div className="dashboard-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button className="btn btn-outline btn-sm" onClick={() => navigate(`/influencer-profiles?username=${username}`)} style={{ display: "flex", alignItems: "center", gap: 6, borderRadius: 8 }}>
              <FontAwesomeIcon icon={faArrowLeft} /> Profile
            </button>
            <div>
              <h1 className="dashboard-title" style={{ marginBottom: 2 }}>
                <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 10, color: "var(--primary-color)" }} />
                Analytics Dashboard
                <span style={{ color: "var(--text-gray)", fontWeight: 400 }}> — @{username}</span>
              </h1>
              <p className="dashboard-subtitle" style={{ margin: 0 }}>
                {allTweets.length} tweets loaded · Page {currentPage}
              </p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={loadMore} disabled={loading || !hasNextPage} style={{ display: "flex", alignItems: "center", gap: 8, height: 40 }}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSync} />}
            Load Next 20 Tweets
          </button>
        </div>

        {/* ═══ FIX 1: PROFILE OVERVIEW — fixed cover/avatar overlap ═ */}
        <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}>
          {/* Cover — increased height */}
          <div style={{
            height: 180,
            background: profile.cover_picture
              ? `url(${profile.cover_picture}) center/cover no-repeat`
              : "linear-gradient(135deg, var(--primary-color), var(--primary-light))",
            position: "relative",
          }}>
            <div style={{
              position: "absolute", top: 14, right: 14,
              backgroundColor: getRankColor(profile.ranking_score), color: "#fff",
              padding: "5px 12px", borderRadius: 16, fontSize: "0.75rem", fontWeight: 700,
              display: "flex", alignItems: "center", gap: 5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}>
              <FontAwesomeIcon icon={faStar} />{profile.ranking_score?.toFixed(1)}
            </div>
          </div>

          {/* Profile content — separate from cover with proper spacing */}
          <div style={{ padding: "0 24px 24px", position: "relative" }}>
            {/* Avatar — pulled up over cover edge */}
            <div style={{
              width: 84, height: 84, borderRadius: "50%",
              border: "4px solid #fff", overflow: "hidden",
              backgroundColor: "var(--background-light)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
              marginTop: -42, position: "relative", zIndex: 2,
            }}>
              {profile.profile_picture ? (
                <img
                  src={profile.profile_picture.replace("_normal", "_400x400")}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "var(--background-light)" }}>
                  <FontAwesomeIcon icon={faUser} style={{ fontSize: "1.5rem", color: "var(--text-gray)" }} />
                </div>
              )}
            </div>

            {/* Name row — below avatar with proper margin */}
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: "1.25rem", fontWeight: 700 }}>{profile.name}</span>
                {profile.is_blue_verified && <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#1DA1F2", fontSize: "1rem" }} />}
                {profile.category && (
                  <span style={{ backgroundColor: "var(--primary-color)", color: "#fff", padding: "2px 10px", borderRadius: 10, fontSize: "0.72rem", fontWeight: 600, textTransform: "capitalize" }}>
                    {profile.category}
                  </span>
                )}
              </div>
              <div style={{ color: "var(--text-gray)", fontSize: "0.88rem", marginTop: 2 }}>@{profile.username}</div>
            </div>

            {profile.description && (
              <p style={{ marginTop: 12, color: "var(--text-light)", fontSize: "0.85rem", lineHeight: 1.55, whiteSpace: "pre-line" }}>
                {profile.description}
              </p>
            )}

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, fontSize: "0.8rem", color: "var(--text-gray)" }}>
              {profile.location && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />{profile.location}
                </span>
              )}
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FontAwesomeIcon icon={faCalendarAlt} />Joined {fmtDateFull(profile.created_at)}
              </span>
            </div>

            {/* Profile metrics row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 10, marginTop: 16 }}>
              {[
                { l: "Followers", v: fmt(profile.metrics?.followers) },
                { l: "Following", v: fmt(profile.metrics?.following) },
                { l: "Tweets", v: fmt(profile.metrics?.tweets_count) },
                { l: "Media", v: fmt(profile.metrics?.media_count) },
                { l: "Ratio", v: a ? fmtDec(a.ffRatio) : "—" },
                { l: "Age", v: a ? a.accountAgeYears + "y" : "—" },
              ].map((m, i) => (
                <div key={i} style={{ padding: "10px 12px", backgroundColor: "var(--background-light)", borderRadius: 8, border: "1px solid var(--border-color)" }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase", letterSpacing: 0.5 }}>{m.l}</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--text-dark)", marginTop: 2 }}>{m.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {a && (
          <>
            {/* ═══ SECTION 2: ENGAGEMENT OVERVIEW ══════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
              <StatCard icon={faEye} label="Total Views" value={fmt(a.totalViews)} iconColor="#1DA1F2" />
              <StatCard icon={faHeart} label="Total Likes" value={fmt(a.totalLikes)} iconColor="#E91E63" />
              <StatCard icon={faComment} label="Total Replies" value={fmt(a.totalReplies)} iconColor="#4CAF50" />
              <StatCard icon={faBookmark} label="Total Bookmarks" value={fmt(a.totalBookmarks)} iconColor="#FF9800" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <HighlightCard label="Avg. Engagement Rate" value={fmtPct(a.avgEngagementRate)} sub={`Across ${a.n} tweets`} bg="var(--primary-color)" />
              <HighlightCard label="Avg. Views per Tweet" value={fmt(Math.round(a.avgViews))} sub={`Reach ratio: ${fmtDec(a.avgReachRatio)}x`} bg="#2C3E50" />
            </div>

            {/* ═══ SECTION 3: TOP TWEETS ═══════════════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 20 }}>
              <MiniTable title="Top by Views" icon={faEye} items={a.topByViews} valueKey="_views" />
              <MiniTable title="Top by Engagement Rate" icon={faBolt} items={a.topByER} valueKey="_engagementRate" valueFormatter={fmtPct} />
              <MiniTable title="Top by Bookmarks" icon={faBookmark} items={a.topByBookmarks} valueKey="_bookmarks" />
            </div>

            {/* ═══ SECTION 4: CLASSIFICATION ═══════════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 3fr", gap: 14, marginBottom: 20 }}>
              <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, margin: 0 }}>
                <h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 20, alignSelf: "flex-start" }}>Classification Distribution</h4>
                <div style={{ position: "relative", width: 160, height: 160 }}>
                  <svg viewBox="0 0 36 36" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                    {(() => {
                      let offset = 0;
                      return a.classData.map((c, i) => {
                        const pct = c.pct * 100;
                        const color = getClassColor(c.name).color;
                        const el = (
                          <circle key={i} cx="18" cy="18" r="15.9155" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={-offset} strokeLinecap="round" />
                        );
                        offset += pct;
                        return el;
                      });
                    })()}
                  </svg>
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 700, color: "var(--text-dark)" }}>{fmt(a.n)}</div>
                    <div style={{ fontSize: "0.65rem", color: "var(--text-gray)", fontWeight: 600, textTransform: "uppercase" }}>Total Tweets</div>
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16, justifyContent: "center" }}>
                  {a.classData.slice(0, 5).map((c) => (
                    <div key={c.name} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getClassColor(c.name).color }} />
                      <span style={{ fontSize: "0.72rem", color: "var(--text-gray)" }}>{c.name} ({(c.pct * 100).toFixed(0)}%)</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: 0, overflow: "hidden", margin: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--background-light)", borderBottom: "1px solid var(--border-color)" }}>
                      {["Class", "Count", "%", "Avg Views", "Avg ER"].map((h) => (
                        <th key={h} style={{ padding: "12px 14px", textAlign: h === "Class" ? "left" : "center", fontSize: "0.68rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {a.classData.map((c, i) => (
                      <tr key={c.name} style={{ borderBottom: i < a.classData.length - 1 ? "1px solid var(--border-color)" : "none" }}>
                        <td style={{ padding: "12px 14px", fontWeight: 700 }}>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: getClassColor(c.name).color, marginRight: 8 }} />
                          {c.name}
                        </td>
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>{c.count}</td>
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>{(c.pct * 100).toFixed(0)}%</td>
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>{fmt(Math.round(c.avgViews))}</td>
                        <td style={{ padding: "12px 14px", textAlign: "center", color: "var(--primary-color)", fontWeight: 700 }}>{fmtPct(c.avgER)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ═══ SECTION 5: POSTING BEHAVIOR ═════════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 14, marginBottom: 20 }}>
              <div className="card" style={{ padding: 20, margin: 0 }}>
                <h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 20 }}>Tweets by Hour (UTC)</h4>
                <div style={{ display: "flex", alignItems: "flex-end", height: 160, gap: 2 }}>
                  {a.hourMap.map((h, i) => {
                    const heightPct = safeDivide(h.count, a.maxHourCount) * 100;
                    const isBest = i === a.bestHourIdx;
                    return (
                      <div key={i} title={`${HOURS_LABELS[i]}: ${h.count} tweets`} style={{ flex: 1, height: `${Math.max(heightPct, 2)}%`, backgroundColor: isBest ? "var(--primary-color)" : "rgba(206,69,43,0.2)", borderRadius: "3px 3px 0 0", transition: "height 0.3s", cursor: "default" }} />
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: "0.65rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>
                  <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="card" style={{ padding: 14, margin: 0 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>Most Active Day</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                    <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>{DAYS[a.mostActiveDayIdx]}</span>
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--primary-color)" }}>Avg {fmtDec(safeDivide(a.dayMap[a.mostActiveDayIdx].count, 1))}/d</span>
                  </div>
                </div>
                <div className="card" style={{ padding: 14, margin: 0 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>Best ER Hour</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                    <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>{HOURS_LABELS[a.bestHourIdx]}</span>
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#28A745" }}>{fmtPct(safeDivide(a.hourMap[a.bestHourIdx].erSum, a.hourMap[a.bestHourIdx].count))}</span>
                  </div>
                </div>
                <div className="card" style={{ padding: 14, margin: 0 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>Peak Reach Day</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                    <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>{DAYS[a.peakReachDayIdx]}</span>
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--primary-color)" }}>{fmt(Math.round(safeDivide(a.dayMap[a.peakReachDayIdx].viewSum, a.dayMap[a.peakReachDayIdx].count)))} avg</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ SECTION 6: SOURCE & LANGUAGE ════════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <div className="card" style={{ padding: 20, margin: 0 }}>
                <h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 20 }}>Device Distribution</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {a.sourceData.map((s) => (
                    <div key={s.name}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", fontWeight: 700, marginBottom: 6 }}>
                        <span>{s.name}</span>
                        <span>{(s.pct * 100).toFixed(0)}%</span>
                      </div>
                      <div style={{ width: "100%", height: 8, backgroundColor: "var(--background-light)", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: `${s.pct * 100}%`, height: "100%", backgroundColor: "var(--primary-color)", borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: 20, margin: 0 }}>
                <h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 20 }}>Language Trends & Avg ER</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {a.langData.map((l) => (
                    <div key={l.code} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", backgroundColor: "var(--background-light)", borderRadius: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", border: "1px solid var(--border-color)" }}>{l.code}</span>
                        <span style={{ fontSize: "0.88rem", fontWeight: 600 }}>{l.code === "en" ? "English" : l.code === "hi" ? "Hindi" : l.code === "und" ? "Undefined" : l.code}</span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--primary-color)" }}>{fmtPct(l.avgER)}</div>
                        <div style={{ fontSize: "0.65rem", color: "var(--text-gray)" }}>{(l.pct * 100).toFixed(0)}% of tweets</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ═══ SECTION 7: REPLY VS ORIGINAL ═══════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <div className="card" style={{ padding: 20, margin: 0, display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(206,69,43,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FontAwesomeIcon icon={faComment} style={{ fontSize: "1.4rem", color: "var(--primary-color)" }} />
                </div>
                <div>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>Replies</div>
                  <div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{a.replies}</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--text-gray)", marginTop: 2 }}>Avg ER: <span style={{ color: "var(--primary-color)", fontWeight: 700 }}>{fmtPct(a.avgERReplies)}</span></div>
                </div>
              </div>
              <div className="card" style={{ padding: 20, margin: 0, display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(108,99,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: "1.4rem", color: "#6C63FF" }} />
                </div>
                <div>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--text-gray)", textTransform: "uppercase" }}>Original Tweets</div>
                  <div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{a.originals}</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--text-gray)", marginTop: 2 }}>Avg ER: <span style={{ color: "#6C63FF", fontWeight: 700 }}>{fmtPct(a.avgEROriginals)}</span></div>
                </div>
              </div>
            </div>

            {/* ═══ SECTION 8: TIMELINE ═════════════════════════════ */}
            {a.timeline.length > 0 && (
              <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                <h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 20 }}>Performance Timeline</h4>
                <div style={{ display: "flex", alignItems: "flex-end", height: 180, gap: 4 }}>
                  {a.timeline.map((d, i) => {
                    const h = safeDivide(d.views, a.maxTimelineViews) * 100;
                    return (
                      <div key={i} title={`${d.date}: ${fmt(d.views)} views, ${d.tweets} tweets`} style={{ flex: 1, height: `${Math.max(h, 3)}%`, backgroundColor: h > 70 ? "var(--primary-color)" : "rgba(206,69,43,0.15)", borderRadius: "4px 4px 0 0", transition: "height 0.3s" }} />
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: "0.65rem", fontWeight: 700, color: "var(--text-gray)" }}>
                  {a.timeline.length > 0 && <span>{a.timeline[0].date}</span>}
                  {a.timeline.length > 1 && <span>{a.timeline[a.timeline.length - 1].date}</span>}
                </div>
              </div>
            )}

            {/* ═══ SECTION 9: BOOKMARK & REACH ════════════════════ */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="card" style={{ padding: 20, margin: 0 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-gray)" }}>Avg Bookmark Rate</div>
                  <div style={{ fontSize: "2rem", fontWeight: 700, marginTop: 6 }}>{fmtPct(a.avgBookmarkRate)}</div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#28A745", marginTop: 4 }}>Save Intent Metric</div>
                </div>
                <div className="card" style={{ padding: 20, margin: 0 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-gray)" }}>View-to-Follower Ratio</div>
                  <div style={{ fontSize: "2rem", fontWeight: 700, marginTop: 6 }}>{fmtDec(a.avgReachRatio)}x</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-gray)", marginTop: 4 }}>Avg reach per tweet</div>
                </div>
              </div>

              <div className="card" style={{ padding: 0, overflow: "hidden", margin: 0 }}>
                <div style={{ padding: "14px 18px", backgroundColor: "var(--background-light)", borderBottom: "1px solid var(--border-color)", fontWeight: 700, fontSize: "0.9rem" }}>
                  <FontAwesomeIcon icon={faFire} style={{ marginRight: 8, color: "var(--primary-color)" }} />
                  Top Virality Tweets
                </div>
                {a.topByVirality.map((t, i) => (
                  <div key={i} style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: i < a.topByVirality.length - 1 ? "1px solid var(--border-color)" : "none" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 500, flex: 1, marginRight: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{trimText(t.text, 80)}</span>
                    <span style={{ fontWeight: 700, fontSize: "0.85rem", color: "var(--primary-color)", flexShrink: 0 }}>{fmt(t._viralityScore)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ═══ SECTION 10: SUMMARY ═════════════════════════════ */}
            {summary && (
              <div style={{ backgroundColor: "rgba(206,69,43,0.05)", border: "1px solid rgba(206,69,43,0.2)", padding: "28px 24px", borderRadius: 12, marginBottom: 20, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, right: 0, padding: 16, opacity: 0.08 }}>
                  <FontAwesomeIcon icon={faLightbulb} style={{ fontSize: "4rem", color: "var(--primary-color)" }} />
                </div>
                <h4 style={{ color: "var(--primary-color)", fontWeight: 700, fontSize: "1.05rem", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <FontAwesomeIcon icon={faLightbulb} /> Content Insights Summary
                </h4>
                <p style={{ color: "var(--text-light)", fontSize: "0.9rem", lineHeight: 1.7, margin: 0 }}>{summary}</p>
              </div>
            )}

            {/* ═══ SECTION 11: FETCH STATUS ════════════════════════ */}
            <div className="card" style={{ padding: "12px 18px", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "var(--text-gray)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#28A745" }} />
                  Page {currentPage} loaded
                </span>
                <span><FontAwesomeIcon icon={faDatabase} style={{ marginRight: 4 }} />{allTweets.length} tweets</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <span>Has Next: {hasNextPage ? "Yes" : "No"}</span>
                <span style={{ padding: "3px 10px", backgroundColor: "var(--background-light)", borderRadius: 4 }}>
                  Status: {hasNextPage ? "More Available" : "Complete"}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default InfluencerAnalytics;